import { useState } from 'react';
import {
  Box,
  Chip,
  ChipProps,
  chipClasses,
  Link,
  Tooltip
} from '@mui/material';
import {
  ChipListGaps,
  truncatedChipListSx
} from 'common/table/styles/truncatedChipListSx';

type TableV3TruncatedChipListProps = {
  items: string[];
  maxItems?: number;
  onChipClick?: (item: string) => void;
  // Drawn filled rather than inverse, so a chip already acting as a filter
  // reads differently from one that is only being displayed. A predicate rather
  // than a list because what counts as the same item is the caller's rule: tags
  // match on their canonical form, so two spellings of one tag are both filled.
  isSelected?: (item: string) => boolean;
  gaps?: ChipListGaps;
};

// A label wider than its cell is cut off with an ellipsis, so the full value
// shows on hover. Only then: on a label that fits, hover text repeats it.
const TruncatedChip: React.FC<ChipProps & { label: string }> = (props) => {
  const [isCutOff, setIsCutOff] = useState(false);

  // Read when the pointer or focus arrives rather than on render, because the
  // width depends on the column, which the user can resize.
  const measure = (event: React.SyntheticEvent<HTMLElement>) => {
    const label = event.currentTarget.querySelector<HTMLElement>(
      `.${chipClasses.label}`
    );
    setIsCutOff(!!label && label.scrollWidth > label.clientWidth);
  };

  return (
    <Tooltip title={isCutOff ? props.label : ''} placement="top" arrow>
      <Chip {...props} onMouseEnter={measure} onFocus={measure} />
    </Tooltip>
  );
};

export const TableV3TruncatedChipList: React.FC<
  TableV3TruncatedChipListProps
> = ({ items, maxItems = 4, onChipClick, isSelected, gaps }) => {
  const [showAll, setShowAll] = useState(false);

  if (items.length === 0) return null;

  const visibleItems = showAll ? items : items.slice(0, maxItems);
  const remainingCount = items.length - maxItems;

  return (
    <Box sx={truncatedChipListSx.container(gaps)}>
      {visibleItems.map((item, index) => (
        <TruncatedChip
          key={`${item}-${index}`}
          label={item}
          size="small"
          color="primary"
          variant={isSelected?.(item) ? 'filled' : 'inverse'}
          onClick={onChipClick ? () => onChipClick(item) : undefined}
          sx={
            onChipClick
              ? truncatedChipListSx.clickableChip
              : truncatedChipListSx.chip
          }
        />
      ))}
      {remainingCount > 0 && (
        <Link
          component="button"
          onClick={() => setShowAll(!showAll)}
          sx={truncatedChipListSx.showMoreLink}
        >
          {showAll ? 'See less' : `+${remainingCount} more`}
        </Link>
      )}
    </Box>
  );
};
